import { Text, Title } from '@brokerbay/elements-web';
import {
  Button,
  DatePicker,
  message,
  Input,
  Modal,
  TimePicker,
} from 'components';
import { startOfDay as startOfDate } from 'date-fns';
import React, { useContext, useMemo, useState } from 'react';
import styled from 'styled-components';
import { saveVirtualTour } from 'api/VirtualTourController';
import VirtualTabContext from './VirtualTabContext';
import AppointmentBookingInternalContext from '../AppointmentBookingInternalContext';

const VirtualTourModal: React.FC<{
  isOpen: boolean;
  setIsOpen: (o: boolean) => void;
}> = ({ isOpen, setIsOpen }) => {
  const { listing } = useContext(AppointmentBookingInternalContext);
  const { coop, fetchVirtualTours } = useContext(VirtualTabContext);

  // Note that this is not using the timezone of the listing since the old virtual showing page did the same
  // Using moment here will use the listing timezone instead
  const startOfDay = useMemo(() => startOfDate(new Date()), []);

  const [name, setName] = useState('');
  const [date, setDate] = useState(new Date());
  const [time, setTime] = useState<Date | null>(null);

  const onSaveTour = async () => {
    if (!coop) return;
    const startTime = new Date(time!);
    startTime.setFullYear(date.getFullYear(), date.getMonth(), date.getDate());
    await saveVirtualTour({
      coop,
      startTime: startTime.toISOString(),
      timezone: listing!.timezone,
      tourName: name,
    });
    setIsOpen(false);
    message.success('Virtual Tour created');
    fetchVirtualTours();
  };

  return (
    <Modal
      width={500}
      title={
        <StyledTitle level={3} dataCy="recordShowingModal-title">
          Create New Tour
        </StyledTitle>
      }
      footer={
        <div>
          <Button
            data-cy="virtualTourModal-cancel-btn"
            type="default"
            onClick={() => setIsOpen(false)}
          >
            Cancel
          </Button>
          <Button
            data-cy="virtualTourModal-save-btn"
            type="primary"
            disabled={!date || !time}
            onClick={onSaveTour}
          >
            Save Tour
          </Button>
        </div>
      }
      centered
      closable
      onCancel={() => setIsOpen(false)}
      open={isOpen}
      data-cy="virtualTour-modal"
      destroyOnClose
    >
      <StyledBody>
        <Text>Tour Name:</Text>
        <Input
          dataCy="virtualTour-edit-tourName"
          value={name}
          onChange={e => setName(e.target.value)}
        />
        <Text>Date:</Text>
        <DatePicker
          value={date}
          onChange={newDate => newDate && setDate(newDate)}
          disabledDate={newDate => newDate < startOfDay}
          data-cy="virtualTour-edit-tourDate"
        />
        <Text>Time:</Text>
        <TimePicker
          showSecond={false}
          showNow={false}
          format={'hh:mm a'}
          value={time}
          onChange={newTime => newTime && setTime(newTime)}
          data-cy="virtualTour-edit-tourTime"
        />
      </StyledBody>
    </Modal>
  );
};

const StyledBody = styled.div`
  display: grid;
  grid-template-columns: 1fr 3fr;
  grid-row-gap: 8px;
`;

const StyledTitle = styled(Title)`
  && {
    margin: 0;
  }
`;

export default VirtualTourModal;
